import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faUserCheck, faStore, faUserXmark } from "@fortawesome/free-solid-svg-icons";


import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import Http from "../../Common/Http"; // Adjust the import path as necessary

// Example colors for each slice


// Bar colors for different metrics
const BAR_COLORS = {
  "SPR Sent": "#8884d8",
  "MRR With QC": "#82ca9d",
  "Department Requisition Sent": "#ffc658",
  "Department Requisition Received": "#ff7300",
  "SR Sent": "#d35400",
  "SR Received": "#2980b9",
  "Issue Sent": "#27ae60",
  "Issue Received": "#c0392b",
  "Ball Charge Body": "#8e44ad",
  "Filter Cake Production": "#e67e22",
  Production: "#3498db",
  "FG Transfer Sent": "#1abc9c",
  "FG Transfer Received": "#f39c12",
  Loss: "#e74c3c",
};

const HeadOffice = () => {
  const navigate = useNavigate();
  const [userUsage, setUserUsage] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [departmentData2, setDepartmentData2] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalDepts, setTotalDepts] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch user usage activity log
        const response = await Http.get(`/user-usages-activity-log`);
        setTotalDepts(response.data);
        const apiData = response.data.data;

        // Format data for pie chart (user usage)
        const formattedPieData = apiData
          .map((item) => ({
            name: `${item.department_name}`,
            value:
              (item.issue_sent || 0) +
              (item.sr_received || 0) +
              (item.spr_sent || 0) +
              (item.mrr_with_qc || 0),
          }))
          .filter((item) => item.value > 0);

        setUserUsage(formattedPieData);

        // Transform data for individual department bar charts
        const transformedDepartmentData = apiData
          .map((department) => {
            const chartData = [];

            // Map all possible metrics to readable names
            const metricMapping = {
              spr_sent: "SPR Sent",
              mrr_with_qc: "MRR With QC",
              department_requisition_sent: "Dept. Req. Sent",
              department_requisition_received: "Dept. Req. Received",
              sr_sent: "SR Sent",
              sr_received: "SR Received",
              issue_sent: "Issue Sent",
              issue_received: "Issue Received",
              ball_charge_body: "Ball Charge Body",
              filter_cake_production: "Filter Cake Prod.",
              production: "Production",
              fg_transfer_sent: "FG Transfer Sent",
              fg_transfer_received: "FG Transfer Received",
              loss: "Loss",
            };

            // Create chart data points for each metric that exists and has a value > 0
            Object.entries(metricMapping).forEach(([key, label]) => {
              if (department[key] && department[key] > 0) {
                chartData.push({
                  metric: label,
                  value: department[key],
                });
              }
            });

            return {
              departmentId: department.department_id,
              departmentName: department.department_name,
              chartData: chartData,
            };
          })
          .filter((dept) => dept.chartData.length > 0); // Only include departments with data

        setDepartmentData(transformedDepartmentData);

        const transformedDepartmentData2 = apiData
          .map((department) => {
            const chartData2 = [];

            // Map all possible metrics to readable names
            const metricMapping2 = {
              department_requisition_sent: "Dept. Req. Sent",
              department_requisition_received: "Dept. Req. Received",
              sr_sent: "SR Sent",
              issue_received: "Issue Received",
              ball_charge_body: "Ball Charge Body",
              filter_cake_production: "Filter Cake Prod.",
              production: "Production",
              fg_transfer_sent: "FG Transfer Sent",
              fg_transfer_received: "FG Transfer Received",
              loss: "Loss",
            };

            // Create chart data points for each metric that exists and has a value > 0
            Object.entries(metricMapping2).forEach(([key, label]) => {
              if (department[key] && department[key] > 0) {
                chartData2.push({
                  metric: label,
                  value: department[key],
                });
              }
            });

            return {
              departmentId: department.department_id,
              departmentName: department.department_name,
              chartData2: chartData2,
            };
          })
          .filter((dept) => dept.chartData2.length > 0); // Only include departments with data

        setDepartmentData2(transformedDepartmentData2);

        // Fetch total number of departments
        // const totalDeptsResponse = await Http.get(`/departments-with-panel`);
        // const totalDeptsData = totalDeptsResponse.data.length;
        // console.log(totalDeptsData);

        // setTotalDepts(totalDeptsData);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load dashboard data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Custom tooltip for pie chart
  const renderPieTooltip = (props) => {
    if (props.active && props.payload && props.payload.length) {
      const data = props.payload[0];
      return (
        <div className="bg-white p-3 border rounded shadow">
          <p className="font-medium">{data.payload.name}</p>
          <p className="text-blue-600">
            Total Activity: <span className="font-bold">{data.value}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for bar charts
  const renderBarTooltip = (props) => {
    if (props.active && props.payload && props.payload.length) {
      const data = props.payload[0];
      return (
        <div className="bg-white p-3 border rounded shadow">
          <p className="font-medium">{props.label}</p>
          <p className="text-blue-600">
            Value: <span className="font-bold">{data.value}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Dashboard of CEO</h5>
        </div>
        <div className="am-pagebody">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "400px" }}
          >
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-3">Loading dashboard data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Dashboard of CEO</h5>
        </div>
        <div className="am-pagebody">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Error!</h4>
            <p>{error}</p>
            <hr />
            <p className="mb-0">
              <button
                className="btn btn-outline-danger"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Dashboard of CEO</h5>
        </div>

        <div className="am-pagebody">
          {/* Summary Cards */}
          <div className="row row-sm mg-b-20">
            <div
              className="col-md-3 clickable-card"
              onClick={() => navigate("/user-usages-activity-log")}
            >
              <div className="card bd-0">
                <div className="card-body pd-20 pd-sm-25">
                  <div className="d-flex align-items-center">
                    <div className="flex-1">
                      <p className="tx-11 tx-uppercase tx-spacing-1 tx-color-02 tx-semibold mg-b-8">
                        Active Departments
                      </p>
                      <div className="d-flex align-items-center">
                        <h3 className="tx-normal tx-rubik mg-b-0">
                          {totalDepts.departments_active}{" "}
                        </h3>
                      </div>
                    </div>
                    <div className="wd-40 wd-md-50 ht-40 ht-md-50 bg-primary tx-white mg-l-auto d-flex align-items-center justify-content-center rounded">
                      <FontAwesomeIcon icon={faUserCheck} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
             <div
              className="col-md-3 clickable-card"
              onClick={() => navigate("/user-usages-activity-log")}
            >
              <div className="card bd-0">
                <div className="card-body pd-20 pd-sm-25">
                  <div className="d-flex align-items-center">
                    <div className="flex-1">
                      <p className="tx-11 tx-uppercase tx-spacing-1 tx-color-02 tx-semibold mg-b-8">
                        Inactive Departments
                      </p>
                      <div className="d-flex align-items-center">
                        <h3 className="tx-normal tx-rubik mg-b-0">
                          {totalDepts.departments_total - totalDepts.departments_active}
                        </h3>
                      </div>
                    </div>
                    <div className="wd-40 wd-md-50 ht-40 ht-md-50 bg-danger tx-white mg-l-auto d-flex align-items-center justify-content-center rounded">
                      {/* <i className="icon ion-ios-grid"></i> */}
                      {/* <i className="fa fa-building"></i> */}
                      {/* <FontAwesomeIcon icon={byPrefixAndName.fas['building-user']} /> */}
                      {/* <FontAwesomeIcon icon="fa-solid fa-building-user" /> */}
                      {/* <FontAwesomeIcon icon={faBuildingUser} /> */}
                      <FontAwesomeIcon icon={faUserXmark} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bd-0">
                <div className="card-body pd-20 pd-sm-25">
                  <div className="d-flex align-items-center">
                    <div className="flex-1">
                      <p className="tx-10 tx-uppercase tx-spacing-1 tx-color-02 tx-semibold mg-b-8">
                        All Store Activities
                      </p>
                      <div className="d-flex align-items-center">
                        <h3 className="tx-normal tx-rubik mg-b-0">
                          {userUsage.reduce((sum, item) => sum + item.value, 0)}
                        </h3>
                      </div>
                    </div>
                    <div className="wd-40 wd-md-50 ht-40 ht-md-50 bg-success tx-white mg-l-auto d-flex align-items-center justify-content-center rounded">
                      {/* <i className="icon ion-ios-pulse"></i> */}
                      <FontAwesomeIcon icon={faStore} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bd-0">
                <div className="card-body pd-20 pd-sm-25">
                  <div className="d-flex align-items-center">
                    <div className="flex-1">
                      <p className="tx-10 tx-uppercase tx-spacing-1 tx-color-02 tx-semibold mg-b-8">
                        Departments Activities
                      </p>
                      <div className="d-flex align-items-center">
                        <h3 className="tx-normal tx-rubik mg-b-0">
                          {departmentData2.reduce(
                            (sum, dept) =>
                              sum +
                              dept.chartData2.reduce(
                                (deptSum, item) => deptSum + item.value,
                                0
                              ),
                            0
                          )}
                        </h3>
                      </div>
                    </div>
                    <div className="wd-40 wd-md-50 ht-40 ht-md-50 bg-info tx-white mg-l-auto d-flex align-items-center justify-content-center rounded">
                      <FontAwesomeIcon icon={faChartLine} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* <div className="col-md-3">
              <div className="card bd-0">
                <div className="card-body pd-20 pd-sm-25">
                  <div className="d-flex align-items-center">
                    <div className="flex-1">
                      <p className="tx-11 tx-uppercase tx-spacing-1 tx-color-02 tx-semibold mg-b-8">
                        Data Updated
                      </p>
                      <div className="d-flex align-items-center">
                        <span className="tx-12 tx-color-03">
                          {new Date().toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="wd-40 wd-md-50 ht-40 ht-md-50 bg-warning tx-white mg-l-auto d-flex align-items-center justify-content-center rounded">
                      <i className="icon ion-ios-calendar"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div> */}
          </div>

          

          {/* Individual Department Bar Charts */}
          {departmentData.length > 0 ? (
            <div className="row row-sm mg-t-15 mg-sm-t-20">
              {departmentData.map((department, index) => (
                <div
                  className="col-md-6 col-xl-4"
                  key={department.departmentId}
                >
                  <div className="card pd-20 pd-sm-30 mg-t-20">
                    <h6 className="card-body-title">
                      {department.departmentName}
                    </h6>
                    {/* <p className="tx-11 tx-color-03 mg-b-20">
                      Department ID: {department.departmentId}
                    </p> */}
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart
                        data={department.chartData}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 80,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="metric"
                          angle={-45}
                          textAnchor="end"
                          height={100}
                          fontSize={10}
                          interval={0}
                          stroke="#666"
                        />
                        <YAxis fontSize={10} stroke="#666" />
                        {/* <Tooltip
                          content={renderBarTooltip}
                          cursor={{ fill: "rgba(136, 132, 216, 0.1)" }}
                        /> */}
                        <Tooltip
                          content={(props) => {
                            if (
                              props.active &&
                              props.payload &&
                              props.payload.length
                            ) {
                              const data = props.payload[0];
                              const metric = data.payload.metric;
                              const color = BAR_COLORS[metric] || "#8884d8";
                              return (
                                <div className="bg-white p-3 border rounded shadow">
                                  <p
                                    className="font-medium"
                                    style={{ color: color }}
                                  >
                                    {props.label}: 
                                  {/* </p>
                                  <p style={{ color: color }}>
                                    Value:{" "} */}
                                    <b className="font-bold">
                                      &nbsp; {data.value}
                                    </b>
                                  </p>
                                </div>
                              );
                            }
                            return null;
                          }}
                          cursor={{ fill: "rgba(136, 132, 216, 0.1)" }}
                        />
                        {/* <Bar 
                          dataKey="value" 
                          fill="#8884d8"
                          radius={[4, 4, 0, 0]}
                          name="Activity Count"
                        /> */}
                        <Bar
                          dataKey="value"
                          radius={[4, 4, 0, 0]}
                          name="Activity Count"
                        >
                          {department.chartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={BAR_COLORS[entry.metric] || "#8884d8"}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="tx-11 tx-color-03 mg-t-10">
                      Total Activities:{" "}
                      {department.chartData.reduce(
                        (sum, item) => sum + item.value,
                        0
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="row row-sm mg-t-15 mg-sm-t-20">
              <div className="col-12">
                <div className="card pd-20 pd-sm-40">
                  <div className="text-center">
                    <div className="wd-80 wd-sm-100 ht-80 ht-sm-100 bg-light rounded-circle d-flex align-items-center justify-content-center mg-x-auto mg-b-20">
                      <i className="icon ion-ios-information-circle tx-40 tx-color-03"></i>
                    </div>
                    <h5>No Department Data Available</h5>
                    <p className="tx-color-03">
                      There is no activity data available for any departments at
                      the moment.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pie Chart for User Usage */}
          {/* {userUsage.length > 0 && (
            <div className="row row-sm mg-t-15 mg-sm-t-20">
              <div className="col-md-6">
                <div className="card pd-20 pd-sm-40">
                  <h6 className="card-body-title">Store Activity Overview</h6>
                  <p className="tx-12 tx-color-03 mg-b-20">
                    Visual representation of total activities across all stores
                  </p>
                  <ResponsiveContainer width="150%" height={500}>
                    <PieChart>
                      <Pie
                        data={userUsage}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={190}
                        fill="#8884d8"
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                        labelLine={false}
                      >
                        {userUsage.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={renderPieTooltip} />
                      <Legend
                        wrapperStyle={{
                          paddingTop: "20px",
                          fontSize: "12px",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )} */}
        </div>

        {/* Footer */}
        <div className="am-footer">
          <span>
            Copyright ©. All Rights Reserved.{" "}
            <a href="http://limerickbd.com/">Limerick Resources Limited</a>
          </span>
        </div>
      </div>
    </div>
  );
};

export default HeadOffice;
